import { DateTime } from 'luxon';

/* eslint-disable @typescript-eslint/no-magic-numbers */
const TIME_TICK_INTERVALS_MINUTES = [15, 30, 60, 120, 180, 240, 360, 720];
const MINIMUM_TICK_COUNT = 2;
const APPROXIMATE_TIME_TICK_WIDTH_PX = 60;

export const calculateCommonTickStep = (domain: number): number => {
  // 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000, ....

  if (!domain) {
    return 10;
  }

  const maxLog10 = Math.ceil(Math.log10(domain));
  const multiplier = Math.pow(10, maxLog10);

  const normalizedMax = domain / multiplier;

  const normalizedRawStep = normalizedMax / 5;

  const step = [0.02, 0.05, 0.1, 0.2].find((s) => normalizedRawStep <= s)!;

  return step * multiplier;
};

export const calculateTickValuesForZeroBasedYAxis = (max: number, min: number, step: number): number[] => {
  const negativeTicks = Array.from({
    length: Math.ceil(Math.abs(min) / step),
  }).map((_, i) => Math.round((-i - 1) * step));
  negativeTicks.reverse();

  const ticks = Array.from({
    length: Math.ceil(max / step) + 1,
  }).map((_, i) => Math.round(i * step));

  return [...negativeTicks, ...ticks];
};

export const calculateTickValuesForDomain = (max: number, min: number, step: number): number[] => {
  const stepAdjustedMin = Math.floor(min / step) * step;
  const stepAdjustedMax = Math.ceil(max / step) * step;

  return Array.from({
    length: Math.round((stepAdjustedMax - stepAdjustedMin) / step) + 1,
  }).map((_, i) => stepAdjustedMin + i * step);
};

export const calculateHourTickValues = (
  min: number | undefined,
  max: number | undefined,
  hourStep: number,
  timeZone?: string
): number[] => {
  if (min === undefined || max === undefined) {
    return [];
  }

  let minDateTime = DateTime.fromMillis(min, timeZone ? { zone: timeZone } : undefined);

  minDateTime = minDateTime.set({
    hour: minDateTime.hour - (minDateTime.hour % hourStep),
    minute: 0,
    second: 0,
    millisecond: 0,
  });

  const tickValues = [];

  while (minDateTime.toMillis() <= max) {
    if (minDateTime.toMillis() >= min) {
      tickValues.push(minDateTime.toMillis());
    }

    minDateTime = minDateTime.plus({ hours: hourStep });
  }

  if (tickValues.length === 0) {
    tickValues.push(min, max);
  }

  return tickValues;
};

export const calculateTimeTickValues = (
  min: number | undefined,
  max: number | undefined,
  availableWidth: number,
  timeZone?: string
): number[] => {
  if (min === undefined || max === undefined) {
    return [];
  }

  const maximumTickCount = Math.max(MINIMUM_TICK_COUNT, Math.floor(availableWidth / APPROXIMATE_TIME_TICK_WIDTH_PX));
  const durationMinutes = (max - min) / 60_000;
  const intervalMinutes =
    TIME_TICK_INTERVALS_MINUTES.find((interval) => durationMinutes / interval <= maximumTickCount) ?? TIME_TICK_INTERVALS_MINUTES.at(-1)!;
  const minDateTime = timeZone ? DateTime.fromMillis(min, { zone: timeZone }) : DateTime.fromMillis(min);
  const minutesIntoDay = minDateTime.hour * 60 + minDateTime.minute + (minDateTime.second * 1000 + minDateTime.millisecond) / 60_000;
  let tickDateTime = minDateTime.startOf('day').plus({ minutes: Math.ceil(minutesIntoDay / intervalMinutes) * intervalMinutes });
  const tickValues: number[] = [];

  while (tickDateTime.toMillis() <= max) {
    tickValues.push(tickDateTime.toMillis());
    tickDateTime = tickDateTime.plus({ minutes: intervalMinutes });
  }

  return tickValues.length > 0 ? tickValues : [min, max];
};

export const calculateTickHours = (xAxisDataSet: number[]): number => {
  const differenceBetweenFirstAndLastInHours = (xAxisDataSet.at(-1)! - xAxisDataSet[0]) / 1000 / 60 / 60;

  if (differenceBetweenFirstAndLastInHours > 10) {
    return 6;
  } else if (differenceBetweenFirstAndLastInHours > 5) {
    return 3;
  } else {
    return 1;
  }
};
